import { mat4 } from "gl-matrix";
import * as gltf from "webgl-gltf";
import { Buffer } from "../shader/buffer";
import { Shader } from "../shader/shader";
import { GL_TRIANGLES } from "../shader/vao";
import { getFilenameFromPath } from "../utils";
import { Group } from "./group";
import { Model } from "./model";
export function gltfLoader(synth, path, options = {}) {
    return new Promise((resolve, reject) => {
        const gl = synth.renderer.gl;
        const shouldScaleToUnit = options.scaleToUnit !== false;
        const shouldCenter = options.center !== false || shouldScaleToUnit;
        gltf
            .loadModel(gl, path)
            .then(gltfModel => {
            // remove '-' and ' ' from filename
            const name = getFilenameFromPath(path).replace(/[- ]/g, "_");
            if (!gltfModel.meshes || gltfModel.meshes.length === 0)
                throw new Error("No meshes found in glTF model");
            const hasMultipleMeshes = gltfModel.meshes.length > 1;
            // Prefer the glTF node name for readability; fall back to mesh index.
            const nodeNames = new Map();
            const nodeMatrices = new Map();
            const toMat4 = (value) => {
                if (Array.isArray(value)) {
                    return mat4.fromValues(value[0], value[1], value[2], value[3], value[4], value[5], value[6], value[7], value[8], value[9], value[10], value[11], value[12], value[13], value[14], value[15]);
                }
                return value;
            };
            if (gltfModel.nodes?.length) {
                const parentCount = new Array(gltfModel.nodes.length).fill(0);
                gltfModel.nodes.forEach(node => {
                    if (typeof node.mesh === "number" && node.name && !nodeNames.has(node.mesh)) {
                        nodeNames.set(node.mesh, node.name);
                    }
                    node.children?.forEach(childIndex => {
                        parentCount[childIndex] += 1;
                    });
                });
                const getLocalMatrix = (node) => node.matrix ? toMat4(node.matrix) : node.localBindTransform ? node.localBindTransform : mat4.create();
                const walk = (nodeIndex, parentMatrix) => {
                    const node = gltfModel.nodes[nodeIndex];
                    const local = getLocalMatrix(node);
                    const world = parentMatrix ? mat4.multiply(mat4.create(), parentMatrix, local) : mat4.clone(local);
                    if (typeof node.mesh === "number" && !nodeMatrices.has(node.mesh)) {
                        nodeMatrices.set(node.mesh, world);
                    }
                    node.children?.forEach(childIndex => walk(childIndex, world));
                };
                gltfModel.nodes.forEach((_, index) => {
                    if (parentCount[index] === 0)
                        walk(index);
                });
            }
            const resolveMeshName = (mesh, index) => {
                const nodeName = nodeNames.get(index);
                // remove "-" and " " from name
                const meshName = (mesh.name || nodeName)?.replace(/[- ]/g, "_");
                return meshName ? `${name}_${meshName}` : `${name}_${index}`;
            };
            const bounds = { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0, initialized: false };
            const boundsFromBuffer = (buffer) => {
                if (!ArrayBuffer.isView(buffer._data))
                    return null;
                const data = buffer._data;
                const size = buffer._size || 3;
                if (data.length < 3 || size < 3)
                    return null;
                let minX = data[0];
                let minY = data[1];
                let minZ = data[2];
                let maxX = data[0];
                let maxY = data[1];
                let maxZ = data[2];
                for (let i = 0; i < data.length; i += size) {
                    const x = data[i];
                    const y = data[i + 1];
                    const z = data[i + 2];
                    if (x < minX)
                        minX = x;
                    if (y < minY)
                        minY = y;
                    if (z < minZ)
                        minZ = z;
                    if (x > maxX)
                        maxX = x;
                    if (y > maxY)
                        maxY = y;
                    if (z > maxZ)
                        maxZ = z;
                }
                return { minX, minY, minZ, maxX, maxY, maxZ };
            };
            const mergeBounds = (next) => {
                if (!bounds.initialized) {
                    Object.assign(bounds, next, { initialized: true });
                    return;
                }
                if (next.minX < bounds.minX)
                    bounds.minX = next.minX;
                if (next.minY < bounds.minY)
                    bounds.minY = next.minY;
                if (next.minZ < bounds.minZ)
                    bounds.minZ = next.minZ;
                if (next.maxX > bounds.maxX)
                    bounds.maxX = next.maxX;
                if (next.maxY > bounds.maxY)
                    bounds.maxY = next.maxY;
                if (next.maxZ > bounds.maxZ)
                    bounds.maxZ = next.maxZ;
            };
            const boundsScaleToUnit = (next) => {
                const sizeX = next.maxX - next.minX;
                const sizeY = next.maxY - next.minY;
                const sizeZ = next.maxZ - next.minZ;
                const maxSize = Math.max(sizeX, sizeY, sizeZ);
                if (maxSize <= 0)
                    return 1;
                const targetSize = 2;
                return targetSize / maxSize;
            };
            const boundsCenter = (next) => ({
                x: (next.minX + next.maxX) * 0.5,
                y: (next.minY + next.maxY) * 0.5,
                z: (next.minZ + next.maxZ) * 0.5,
            });
            const buildModel = (mesh, index) => {
                const meshMaterial = typeof mesh.material !== "undefined" ? gltfModel.materials[mesh.material] : null;
                const meshName = resolveMeshName(mesh, index);
                const attributes = {};
                let cells = null;
                if (mesh.positions)
                    attributes.position = new Buffer(synth, { ...mesh.positions, name: meshName + "_position" });
                if (mesh.indices)
                    cells = new Buffer(synth, { ...mesh.indices, name: meshName + "_indices" });
                if (mesh.normals)
                    attributes.normal = new Buffer(synth, { ...mesh.normals, name: meshName + "_normals" });
                if (mesh.texCoord)
                    attributes.texCoord = new Buffer(synth, { ...mesh.texCoord, name: meshName + "_texCoord" });
                if (mesh.tangents)
                    attributes.tangent = new Buffer(synth, { ...mesh.tangents, name: meshName + "_tangents" });
                if (cells)
                    cells.name = meshName + "_indices";
                const modelMatrix = mat4.create();
                const positionBounds = attributes.position ? boundsFromBuffer(attributes.position) : null;
                if (positionBounds && hasMultipleMeshes)
                    mergeBounds(positionBounds);
                if (positionBounds && !hasMultipleMeshes) {
                    if (shouldScaleToUnit) {
                        const scale = boundsScaleToUnit(positionBounds);
                        mat4.scale(modelMatrix, modelMatrix, [scale, scale, scale]);
                    }
                    if (shouldCenter) {
                        const center = boundsCenter(positionBounds);
                        mat4.translate(modelMatrix, modelMatrix, [-center.x, -center.y, -center.z]);
                    }
                }
                const nodeMatrix = nodeMatrices.get(index);
                if (nodeMatrix) {
                    mat4.multiply(modelMatrix, nodeMatrix, modelMatrix);
                }
                return new Model(synth, {
                    name: meshName,
                    material: meshMaterial,
                    mesh,
                    modelMatrix,
                    vao: {
                        attrs: attributes,
                        cells,
                        elements: mesh.elementCount || (cells ? cells._data.length : 0),
                        primitive: GL_TRIANGLES,
                    },
                });
            };
            if (gltfModel.meshes.length > 1) {
                const group = new Group(synth, { name });
                const models = [];
                gltfModel.meshes.forEach((mesh, index) => {
                    const model = buildModel(mesh, index);
                    if (mesh.texCoord)
                        model.useTexCoord(true);
                    models.push(model);
                    group.add(model);
                });
                if (shouldScaleToUnit && bounds.initialized) {
                    const scale = boundsScaleToUnit(bounds);
                    models.forEach(model => {
                        mat4.scale(model.modelMatrix, model.modelMatrix, [scale, scale, scale]);
                    });
                }
                if (shouldCenter && bounds.initialized) {
                    const center = boundsCenter(bounds);
                    models.forEach(model => {
                        mat4.translate(model.modelMatrix, model.modelMatrix, [-center.x, -center.y, -center.z]);
                    });
                }
                resolve(group);
                return;
            }
            const model = buildModel(gltfModel.meshes[0], 0);
            if (gltfModel.meshes[0].texCoord)
                model.useTexCoord(true);
            resolve(model);
        })
            .catch(reject);
    });
}
export function fragLoader(synth, path, props) {
    return new Promise((resolve, reject) => {
        fetch(path)
            .then(response => response.text())
            .then((data) => {
            const result = new Shader(synth, {
                ...props,
                fragment: data,
                uniforms: [
                    {
                        name: "time",
                        type: "float",
                        value: () => synth.time,
                    },
                    {
                        name: "resolution",
                        type: "vec2",
                        value: () => [synth.width, synth.height],
                    },
                    {
                        name: "mouse",
                        type: "vec2",
                        value: () => [synth.mouseX, synth.mouseY],
                    },
                ],
            });
            resolve(result);
        })
            .catch(reject);
    });
}
